<template>
  <div class="reaccept-page">
    <div class="reaccept-card">
      <h1 class="reaccept-title">Our Terms have updated</h1>
      <p class="reaccept-body">
        We&rsquo;ve made changes to the Kiey Terms of Service. Please review the
        current version and accept to continue using your account.
      </p>

      <div class="reaccept-meta">
        <p>
          <strong>New version:</strong>
          <code class="reaccept-version">{{ tos_current_version }}</code>
        </p>
        <p v-if="user && user.tos_accepted_version">
          <strong>You previously accepted:</strong>
          <code class="reaccept-version">{{ user.tos_accepted_version }}</code>
        </p>
      </div>

      <p class="reaccept-links">
        <a href="/terms_and_conditions" target="_blank" rel="noopener">View Terms of Service</a>
        &middot;
        <a href="/privacy_policy" target="_blank" rel="noopener">Privacy Policy</a>
      </p>

      <label class="reaccept-checkbox">
        <input type="checkbox" v-model="agreed" />
        <span>
          I have reviewed and agree to the updated
          <a href="/terms_and_conditions" target="_blank" rel="noopener">Terms of Service</a>
          and
          <a href="/privacy_policy" target="_blank" rel="noopener">Privacy Policy</a>.
        </span>
      </label>

      <p v-if="error" class="reaccept-error">{{ error }}</p>

      <div class="reaccept-actions">
        <button
          type="button"
          class="reaccept-btn"
          :disabled="!agreed || submitting"
          @click="onAccept"
        >
          {{ submitting ? 'Saving...' : 'Accept and continue' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import config from "@/config/constants.js";

export default {
  name: "ReacceptTerms",
  data() {
    return {
      agreed: false,
      submitting: false,
      error: "",
      tos_current_version: config.tos_current_version,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.Authenticate.user,
    }),
  },
  methods: {
    async onAccept() {
      if (!this.agreed || this.submitting) return;
      this.submitting = true;
      this.error = "";
      try {
        const token = localStorage.getItem("auth_token");
        const headers = token ? { Authorization: token } : {};
        const response = await axios.put(
          config.api_url + "users/me/tos_accept",
          {
            tos_accepted: true,
            tos_version: this.tos_current_version,
          },
          { headers }
        );
        if (response.data && response.data.code === "SUCCESS") {
          // Update the in-memory auth user so the router gate stops
          // bouncing them here. Field name matches what activate.js
          // writes to users/{uid}.
          this.$store.commit("Authenticate/updateUser", {
            tos_accepted_version: this.tos_current_version,
            tos_accepted_at: new Date(),
          });
          // Re-route to wherever they were originally headed, or the
          // dashboard. Falling through to '/' lets the dashboard guard
          // resolve based on role + payment status.
          const redirect = this.$route.query.redirect || "/";
          this.$router.replace(redirect);
        } else {
          this.error =
            (response.data && response.data.message) ||
            "Could not record your acceptance. Please try again.";
        }
      } catch (err) {
        console.error("[reaccept] failed:", err);
        this.error =
          (err.response && err.response.data && err.response.data.message) ||
          "Could not record your acceptance. Please try again.";
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.reaccept-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fb;
  padding: 24px 16px;
  box-sizing: border-box;
}
.reaccept-card {
  max-width: 560px;
  width: 100%;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(6, 29, 71, 0.08);
  padding: 40px 36px;
  box-sizing: border-box;
}
.reaccept-title {
  color: #061d47;
  font-family: "Roboto-Medium", Arial, sans-serif;
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 12px;
}
.reaccept-body {
  color: #2d2d2d;
  font-size: 15px;
  line-height: 1.5;
  margin: 0 0 20px;
}
.reaccept-meta {
  background: #eef0f5;
  border-radius: 8px;
  padding: 14px 16px;
  margin: 0 0 20px;
}
.reaccept-meta p {
  margin: 0;
  font-size: 13px;
  color: #2d2d2d;
}
.reaccept-meta p + p {
  margin-top: 6px;
}
.reaccept-version {
  background: #fff;
  border-radius: 4px;
  padding: 1px 6px;
  font-family: monospace;
  font-size: 12px;
  margin-left: 4px;
}
.reaccept-links {
  font-size: 13px;
  margin: 0 0 18px;
}
.reaccept-links a {
  color: #29abe2;
  text-decoration: none;
}
.reaccept-links a:hover {
  text-decoration: underline;
}
.reaccept-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  line-height: 1.45;
  color: #2d2d2d;
  cursor: pointer;
  margin: 0 0 16px;
}
.reaccept-checkbox input[type="checkbox"] {
  margin-top: 4px;
  flex: 0 0 auto;
  cursor: pointer;
}
.reaccept-checkbox a {
  color: #29abe2;
  text-decoration: none;
}
.reaccept-checkbox a:hover {
  text-decoration: underline;
}
.reaccept-error {
  color: #c0392b;
  font-size: 13px;
  margin: 0 0 12px;
}
.reaccept-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}
.reaccept-btn {
  background: #29abe2;
  color: #fff;
  border: none;
  border-radius: 24px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  min-width: 200px;
}
.reaccept-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.reaccept-btn:not(:disabled):hover {
  background: #1f9bd0;
}
@media (max-width: 600px) {
  .reaccept-card { padding: 28px 20px; }
  .reaccept-title { font-size: 20px; }
  .reaccept-btn { width: 100%; }
}
</style>
